import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  CoworkSession,
  CoworkSessionSummary,
  CoworkMessage,
  CoworkConfig,
  CoworkPermissionRequest,
  CoworkSessionStatus,
  CoworkExecutionMode,
  CoworkPermissionMode,
  CoworkQueuedMessage,
  CoworkDocumentExtractProgressEvent,
} from '../../types/cowork';

interface CoworkState {
  sessions: CoworkSessionSummary[];
  currentSessionId: string | null;
  currentSession: CoworkSession | null;
  draftPrompt: string;
  unreadSessionIds: string[];
  isCoworkActive: boolean;
  isStreaming: boolean;
  pendingPermissions: CoworkPermissionRequest[];
  config: CoworkConfig;
  /** Sandbox 侧通过 IPC 实时上报的交付物路径（按 sessionId 累积，纯路径字符串）。 */
  registeredDeliverables: Record<string, string[]>;
  /** 当前轮尚未结束时追加的后续消息，按会话 FIFO 排队。 */
  queuedMessagesBySession: Record<string, CoworkQueuedMessage[]>;
  /** DocumentExtract 解析进度（瞬态，不持久化），sessionId → toolUseId → 最新进度。 */
  documentExtractProgressBySession: Record<string, Record<string, CoworkDocumentExtractProgressEvent>>;
  /** 每会话最近一条 DocumentExtract 进度（续读调用等下一批时沿用同一文件的页数）。 */
  documentExtractLatestBySession: Record<string, CoworkDocumentExtractProgressEvent>;

  // ── 新建任务的执行环境（云端沙箱 / relay 桌面设备）──
  // 侧边栏的环境选择器与 composer 的环境选择器共用这一份状态：两者本质是同一个概念，
  // 各存各的会出现「侧边栏选了桌面设备、composer 还在显示沙箱选项」这种自相矛盾的界面。
  /** 选中的桌面设备 clientId；null = 云端沙箱。 */
  selectedDesktopClientId: string | null;
  /** 设备名快照——relay WS 重连导致客户端列表短暂为空时，按钮文案不至于闪回默认值。 */
  selectedDesktopClientName: string | null;
  /** 桌面设备上选定的工作目录；null = 交给桌面端默认目录。 */
  selectedDesktopCwd: string | null;
  /** 新建 Desktop Cowork 会话时使用的权限模式。 */
  selectedDesktopPermissionMode: CoworkPermissionMode;

  /** 新建任务的提示档位：collaboration=完整 Agent；quick=仅携带记忆的轻量对话。 */
  newTaskPromptMode: 'collaboration' | 'quick';
}

const initialState: CoworkState = {
  sessions: [],
  currentSessionId: null,
  currentSession: null,
  draftPrompt: '',
  unreadSessionIds: [],
  isCoworkActive: false,
  isStreaming: false,
  pendingPermissions: [],
  registeredDeliverables: {},
  queuedMessagesBySession: {},
  documentExtractProgressBySession: {},
  documentExtractLatestBySession: {},
  selectedDesktopClientId: null,
  selectedDesktopClientName: null,
  selectedDesktopCwd: null,
  selectedDesktopPermissionMode: 'auto',
  newTaskPromptMode: 'collaboration',
  config: {
    workingDirectory: '',
    systemPrompt: '',
    executionMode: 'sandbox',
    memoryEnabled: true,
    memoryImplicitUpdateEnabled: true,
    memoryGuardLevel: 'strict',
    memoryUserMemoriesMaxItems: 12,
    sandboxEnvVars: {},
  },
};

const markSessionRead = (state: CoworkState, sessionId: string | null) => {
  if (!sessionId) return;
  state.unreadSessionIds = state.unreadSessionIds.filter((id) => id !== sessionId);
};

const markSessionUnread = (state: CoworkState, sessionId: string) => {
  if (state.currentSessionId === sessionId) return;
  if (state.unreadSessionIds.includes(sessionId)) return;
  state.unreadSessionIds.push(sessionId);
};

const coworkSlice = createSlice({
  name: 'cowork',
  initialState,
  reducers: {
    /** 登录周期切换后丢弃上一账号的会话、审批、队列、执行端和产物快照。 */
    resetCoworkState: () => initialState,
    setCoworkActive(state, action: PayloadAction<boolean>) {
      state.isCoworkActive = action.payload;
    },

    setNewTaskPromptMode(state, action: PayloadAction<'collaboration' | 'quick'>) {
      state.newTaskPromptMode = action.payload;
    },

    updateSessionPromptMode(state, action: PayloadAction<{ sessionId: string; promptMode: 'collaboration' | 'quick' }>) {
      if (state.currentSession?.id === action.payload.sessionId) {
        state.currentSession.promptMode = action.payload.promptMode;
      }
    },

    setSessions(state, action: PayloadAction<CoworkSessionSummary[]>) {
      state.sessions = action.payload;
      const validSessionIds = new Set(action.payload.map((session) => session.id));
      state.unreadSessionIds = state.unreadSessionIds.filter((id) => {
        return validSessionIds.has(id) && id !== state.currentSessionId;
      });
    },

    setCloudSessions(state, action: PayloadAction<CoworkSessionSummary[]>) {
      const desktopSessions = state.sessions.filter((session) => session.origin === 'desktop');
      const desktopIds = new Set(desktopSessions.map((session) => session.id));
      const cloudSessions = action.payload.filter((session) => !desktopIds.has(session.id));
      state.sessions = [...desktopSessions, ...cloudSessions];
      const validSessionIds = new Set(state.sessions.map((session) => session.id));
      state.unreadSessionIds = state.unreadSessionIds.filter((id) => {
        return validSessionIds.has(id) && id !== state.currentSessionId;
      });
    },

    upsertDesktopSession(state, action: PayloadAction<CoworkSessionSummary>) {
      const session = { ...action.payload, origin: 'desktop' as const };
      const sessionIndex = state.sessions.findIndex((item) => item.id === session.id);
      if (sessionIndex === -1) {
        state.sessions.unshift(session);
      } else {
        state.sessions[sessionIndex] = { ...state.sessions[sessionIndex], ...session };
      }
      if (state.currentSession?.id === session.id && state.currentSession.origin === 'desktop') {
        state.currentSession.status = session.status;
      }
      if (state.currentSessionId === session.id) {
        state.isStreaming = session.status === 'running';
      }
    },

    patchDesktopSession(state, action: PayloadAction<{
      sessionId: string;
      patch: Partial<Omit<CoworkSessionSummary, 'id' | 'origin'>>;
    }>) {
      const sessionIndex = state.sessions.findIndex((session) => session.id === action.payload.sessionId);
      if (sessionIndex === -1 || state.sessions[sessionIndex].origin !== 'desktop') return;
      state.sessions[sessionIndex] = {
        ...state.sessions[sessionIndex],
        ...action.payload.patch,
        origin: 'desktop',
      };
      if (state.currentSession?.id === action.payload.sessionId) {
        state.currentSession = { ...state.currentSession, ...action.payload.patch, origin: 'desktop' };
      }
    },

    setCurrentSessionId(state, action: PayloadAction<string | null>) {
      const sessionId = action.payload;
      state.currentSessionId = sessionId;
      if (state.currentSession?.id !== sessionId) {
        state.currentSession = null;
      }
      const selectedSummary = state.sessions.find((session) => session.id === sessionId);
      state.isStreaming = selectedSummary?.status === 'running';
      markSessionRead(state, sessionId);
    },

    setCurrentSession(state, action: PayloadAction<CoworkSession | null>) {
      state.currentSession = action.payload;
      if (action.payload) {
        state.currentSessionId = action.payload.id;
        state.isStreaming = action.payload.status === 'running';
        if (!action.payload.id.startsWith('temp-')) {
          const { id, title, status, pinned, origin, modelId, permissionMode, imMetadata, createdAt, updatedAt } = action.payload;
          const summary: CoworkSessionSummary = {
            id,
            title,
            status,
            pinned: pinned ?? false,
            origin,
            modelId,
            permissionMode,
            imMetadata,
            createdAt,
            updatedAt,
          };
          const sessionIndex = state.sessions.findIndex((session) => session.id === id);
          if (sessionIndex !== -1) {
            state.sessions[sessionIndex] = {
              ...state.sessions[sessionIndex],
              ...summary,
            };
          } else {
            state.sessions.unshift(summary);
          }
        }
        markSessionRead(state, action.payload.id);
      }
    },

    updateCurrentSessionUsage(state, action: PayloadAction<{
      sessionId: string;
      usage: CoworkSession['usage'];
    }>) {
      const { sessionId, usage } = action.payload;
      if (state.currentSession?.id !== sessionId) return;
      state.currentSession.usage = usage;
    },

    setDraftPrompt(state, action: PayloadAction<string>) {
      state.draftPrompt = action.payload;
    },

    /**
     * 切换新建任务的执行环境。
     * @param payload.clientId   桌面设备 clientId；null 表示回到云端沙箱
     * @param payload.deviceName 设备名快照（可选，用于列表为空时的兜底展示）
     * 切换环境必然清空已选工作目录——上一台设备上的路径在新环境里多半不存在。
     */
    setSelectedDesktopClient(state, action: PayloadAction<{ clientId: string | null; deviceName?: string | null }>) {
      const { clientId, deviceName } = action.payload;
      if (state.selectedDesktopClientId !== clientId) {
        state.selectedDesktopCwd = null;
      }
      state.selectedDesktopClientId = clientId;
      state.selectedDesktopClientName = clientId ? (deviceName ?? null) : null;
    },

    /** 设置桌面设备上选定的工作目录；null = 用桌面端默认目录。 */
    setSelectedDesktopCwd(state, action: PayloadAction<string | null>) {
      state.selectedDesktopCwd = action.payload;
    },

    /** 设置下一次新建 Desktop Cowork 会话使用的权限模式。 */
    setSelectedDesktopPermissionMode(state, action: PayloadAction<CoworkPermissionMode>) {
      state.selectedDesktopPermissionMode = action.payload;
    },

    addSession(state, action: PayloadAction<CoworkSession>) {
      const session = action.payload;

      // If replacing a temp session, preserve its messages so the first
      // user message is not lost when the real session arrives with an empty array.
      if (state.currentSession?.id?.startsWith('temp-') && session.messages.length === 0) {
        session.messages = state.currentSession.messages;
      }

      const summary: CoworkSessionSummary = {
        id: session.id,
        title: session.title,
        status: session.status,
        pinned: session.pinned ?? false,
        origin: session.origin,
        modelId: session.modelId,
        permissionMode: session.permissionMode,
        imMetadata: session.imMetadata,
        createdAt: session.createdAt,
        updatedAt: session.updatedAt,
      };
      const existingSessionIndex = state.sessions.findIndex((item) => item.id === session.id);
      if (existingSessionIndex === -1) {
        state.sessions.unshift(summary);
      } else {
        state.sessions[existingSessionIndex] = { ...state.sessions[existingSessionIndex], ...summary };
      }
      state.currentSession = session;
      state.currentSessionId = session.id;
      markSessionRead(state, session.id);
    },

    updateSessionStatus(state, action: PayloadAction<{ sessionId: string; status: CoworkSessionStatus }>) {
      const { sessionId, status } = action.payload;

      // Update in sessions list
      const sessionIndex = state.sessions.findIndex(s => s.id === sessionId);
      if (sessionIndex !== -1) {
        state.sessions[sessionIndex].status = status;
        state.sessions[sessionIndex].updatedAt = Date.now();
      }

      // Update current session if applicable
      if (state.currentSession?.id === sessionId) {
        state.currentSession.status = status;
        state.currentSession.updatedAt = Date.now();
        // Streaming state is tied to the currently opened session only
        state.isStreaming = status === 'running';
      }
    },

    upsertCoworkDocumentExtractProgress(state, action: PayloadAction<CoworkDocumentExtractProgressEvent>) {
      const event = action.payload;
      const sessionItems = state.documentExtractProgressBySession[event.sessionId]
        ?? (state.documentExtractProgressBySession[event.sessionId] = {});
      sessionItems[event.toolUseId] = event;
      state.documentExtractLatestBySession[event.sessionId] = event;
    },

    deleteSession(state, action: PayloadAction<string>) {
      const sessionId = action.payload;
      state.pendingPermissions = state.pendingPermissions.filter(item => item.sessionId !== sessionId);
      delete state.documentExtractProgressBySession[sessionId];
      delete state.documentExtractLatestBySession[sessionId];
      state.sessions = state.sessions.filter(s => s.id !== sessionId);
      state.unreadSessionIds = state.unreadSessionIds.filter((id) => id !== sessionId);
      delete state.queuedMessagesBySession[sessionId];

      if (state.currentSessionId === sessionId) {
        state.currentSessionId = null;
        state.currentSession = null;
        state.isStreaming = false;
      }
    },

    addMessage(state, action: PayloadAction<{ sessionId: string; message: CoworkMessage }>) {
      const { sessionId, message } = action.payload;

      if (state.currentSession?.id === sessionId) {
        // 服务端广播回来的用户消息(非 local- 前缀)若能匹配到同内容的乐观消息,
        // 就原地替换,避免出现两条重复的用户气泡。
        if (message.type === 'user' && !message.id.startsWith('local-')) {
          const localIdx = state.currentSession.messages.findIndex(
            (m) =>
              m.type === 'user' &&
              m.id.startsWith('local-') &&
              m.content === message.content,
          );
          if (localIdx >= 0) {
            state.currentSession.messages[localIdx] = message;
            state.currentSession.updatedAt = message.timestamp;
            const si = state.sessions.findIndex((s) => s.id === sessionId);
            if (si !== -1) state.sessions[si].updatedAt = message.timestamp;
            markSessionUnread(state, sessionId);
            return;
          }
        }

        const exists = state.currentSession.messages.some((item) => item.id === message.id);
        if (!exists) {
          state.currentSession.messages.push(message);
          state.currentSession.updatedAt = message.timestamp;
        }
      }

      // Update session in list
      const sessionIndex = state.sessions.findIndex(s => s.id === sessionId);
      if (sessionIndex !== -1) {
        state.sessions[sessionIndex].updatedAt = message.timestamp;
      }

      markSessionUnread(state, sessionId);
    },

    /** 删除回执与 WS 共用幂等投影，不影响其他会话、产物和已结算用量。 */
    removeSessionMessages(state, action: PayloadAction<{ sessionId: string; deletedMessageIds: string[] }>) {
      if (state.currentSession?.id !== action.payload.sessionId) return;
      const deleted = new Set(action.payload.deletedMessageIds);
      state.currentSession.messages = state.currentSession.messages.filter(message => !deleted.has(message.id));
    },

    updateMessageContent(state, action: PayloadAction<{ sessionId: string; messageId: string; content: string; metadata?: Record<string, unknown> }>) {
      const { sessionId, messageId, content, metadata } = action.payload;

      if (state.currentSession?.id === sessionId) {
        const messageIndex = state.currentSession.messages.findIndex(m => m.id === messageId);
        if (messageIndex !== -1) {
          state.currentSession.messages[messageIndex].content = content;
          // 服务端只回 epoch 等结构化字段(如 sandboxLifecycle)，这里合并进 metadata，
          // 供前端按本地时区统一渲染时刻。
          if (metadata) {
            state.currentSession.messages[messageIndex].metadata = {
              ...(state.currentSession.messages[messageIndex].metadata || {}),
              ...metadata,
            };
          }
        }
      }

      markSessionUnread(state, sessionId);
    },

    updateMessageMetadata(state, action: PayloadAction<{ sessionId: string; messageId: string; metadata: Record<string, unknown> }>) {
      const { sessionId, messageId, metadata } = action.payload;
      if (state.currentSession?.id !== sessionId) return;
      const message = state.currentSession.messages.find(item => item.id === messageId);
      if (message) message.metadata = { ...message.metadata, ...metadata };
    },

    setStreaming(state, action: PayloadAction<boolean>) {
      state.isStreaming = action.payload;
    },

    updateSessionPinned(state, action: PayloadAction<{ sessionId: string; pinned: boolean }>) {
      const { sessionId, pinned } = action.payload;
      const sessionIndex = state.sessions.findIndex(s => s.id === sessionId);
      if (sessionIndex !== -1) {
        state.sessions[sessionIndex].pinned = pinned;
      }
      if (state.currentSession?.id === sessionId) {
        state.currentSession.pinned = pinned;
      }
    },

    updateSessionTitle(state, action: PayloadAction<{ sessionId: string; title: string }>) {
      const { sessionId, title } = action.payload;
      const sessionIndex = state.sessions.findIndex(s => s.id === sessionId);
      if (sessionIndex !== -1) {
        state.sessions[sessionIndex].title = title;
        state.sessions[sessionIndex].updatedAt = Date.now();
      }
      if (state.currentSession?.id === sessionId) {
        state.currentSession.title = title;
        state.currentSession.updatedAt = Date.now();
      }
    },

    enqueuePendingPermission(state, action: PayloadAction<CoworkPermissionRequest>) {
      const alreadyQueued = state.pendingPermissions.some(
        (permission) => permission.requestId === action.payload.requestId
      );
      if (alreadyQueued) return;
      state.pendingPermissions.push(action.payload);
    },

    dequeuePendingPermission(state, action: PayloadAction<{ requestId?: string } | undefined>) {
      const requestId = action.payload?.requestId;
      if (!requestId) {
        state.pendingPermissions.shift();
        return;
      }
      state.pendingPermissions = state.pendingPermissions.filter(
        (permission) => permission.requestId !== requestId
      );
    },

    setQueuedMessages(state, action: PayloadAction<{
      sessionId: string;
      queued: Array<Omit<CoworkQueuedMessage, 'sessionId'>>;
    }>) {
      const { sessionId, queued } = action.payload;
      if (queued.length === 0) {
        delete state.queuedMessagesBySession[sessionId];
        return;
      }
      state.queuedMessagesBySession[sessionId] = queued.map((item) => ({
        ...item,
        sessionId,
      }));
    },

    clearQueuedMessages(state, action: PayloadAction<string>) {
      delete state.queuedMessagesBySession[action.payload];
    },

    clearPendingPermissions(state) {
      state.pendingPermissions = [];
    },

    setConfig(state, action: PayloadAction<CoworkConfig>) {
      state.config = action.payload;
    },

    updateConfig(state, action: PayloadAction<Partial<CoworkConfig>>) {
      state.config = { ...state.config, ...action.payload };
    },

    updateSessionExecutionMode(state, action: PayloadAction<{ sessionId: string; executionMode: CoworkExecutionMode }>) {
      const { sessionId, executionMode } = action.payload;
      if (state.currentSession?.id === sessionId) {
        state.currentSession.executionMode = executionMode;
      }
    },

    updateSessionModelId(state, action: PayloadAction<{ sessionId: string; modelId: string }>) {
      const { sessionId, modelId } = action.payload;
      const sessionIndex = state.sessions.findIndex((session) => session.id === sessionId);
      if (sessionIndex !== -1) {
        state.sessions[sessionIndex].modelId = modelId;
      }
      if (state.currentSession?.id === sessionId) {
        state.currentSession.modelId = modelId;
      }
    },

    /** 更新指定 Desktop 会话的权限模式，并同步列表摘要。 */
    updateSessionPermissionMode(state, action: PayloadAction<{
      sessionId: string;
      permissionMode: CoworkPermissionMode;
    }>) {
      const { sessionId, permissionMode } = action.payload;
      const sessionIndex = state.sessions.findIndex((session) => session.id === sessionId);
      if (sessionIndex !== -1) {
        state.sessions[sessionIndex].permissionMode = permissionMode;
      }
      if (state.currentSession?.id === sessionId) {
        state.currentSession.permissionMode = permissionMode;
      }
    },

    clearCurrentSession(state) {
      state.currentSessionId = null;
      state.currentSession = null;
      state.isStreaming = false;
    },

    addRegisteredDeliverables(state, action: PayloadAction<{ sessionId: string; paths: string[] }>) {
      const { sessionId, paths } = action.payload;
      if (!sessionId || !Array.isArray(paths) || paths.length === 0) return;
      const existing = state.registeredDeliverables[sessionId] ?? [];
      const newPaths = paths.filter((p) => typeof p === 'string' && !existing.includes(p));
      if (newPaths.length > 0) {
        state.registeredDeliverables[sessionId] = [...existing, ...newPaths];
      }
    },
  },
});

export const {
  removeSessionMessages,
  upsertCoworkDocumentExtractProgress,
  setCoworkActive,
  setSessions,
  setCloudSessions,
  upsertDesktopSession,
  patchDesktopSession,
  setCurrentSessionId,
  setCurrentSession,
  updateCurrentSessionUsage,
  setDraftPrompt,
  setSelectedDesktopClient,
  setSelectedDesktopCwd,
  setSelectedDesktopPermissionMode,
  addSession,
  updateSessionStatus,
  deleteSession,
  addMessage,
  updateMessageContent,
  updateMessageMetadata,
  setStreaming,
  updateSessionPinned,
  updateSessionTitle,
  enqueuePendingPermission,
  dequeuePendingPermission,
  setQueuedMessages,
  clearQueuedMessages,
  clearPendingPermissions,
  resetCoworkState,
  setConfig,
  updateConfig,
  updateSessionExecutionMode,
  updateSessionModelId,
  updateSessionPermissionMode,
  clearCurrentSession,
  addRegisteredDeliverables,
  setNewTaskPromptMode,
  updateSessionPromptMode,
} = coworkSlice.actions;

export default coworkSlice.reducer;
